import React, { useCallback, useMemo, useState } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { Brand } from '~/consts/brands';

import { LIST_VISIBILITY } from '../../../consts/schedule';
import { scheduleForBrandAndType } from '../../../utils/scheduleForBrandAndType';
import NewScheduleForm from '../../Common/Forms/NewScheduleForm';

type Props = {
  brands: Array<Brand>;
  offerId: string;
  shouldMigrateSchedule: boolean;
  schedules: App.Offer['brand_schedules'];
};

type ScheduleRange = { start: string; end: string };
type ApplyToAllPayload = { schedule: ScheduleRange };

export default function NewScheduleByBrand({ schedules, brands, offerId, shouldMigrateSchedule }: Props) {
  const [editing, setEditing] = useState(false);
  const [appliedSchedule, setAppliedSchedule] = useState<ScheduleRange | null>(null);

  const onSuccess = useCallback(() => {
    setEditing(false);
  }, []);

  const onSave = useCallback(() => {
    setEditing(true);
  }, []);

  const onApplyToAll = useCallback((payload: ApplyToAllPayload) => {
    setAppliedSchedule(payload.schedule);
  }, []);

  const scheduleIdsByBrand = useMemo(
    () =>
      schedules.reduce<Record<string, Array<number>>>((acc, schedule) => {
        if (schedule.type !== LIST_VISIBILITY) {
          return acc;
        }
        if (!acc[schedule.brand]) {
          acc[schedule.brand] = [];
        }
        acc[schedule.brand].push(schedule.id);
        return acc;
      }, {}),
    [schedules],
  );

  const getScheduleValue = useCallback(
    (brandValue: string, scheduleKey: 'start' | 'end') => {
      if (appliedSchedule) {
        return appliedSchedule[scheduleKey];
      }
      return scheduleForBrandAndType({
        schedules,
        brand: brandValue,
        type: LIST_VISIBILITY,
        scheduleKey,
      });
    },
    [appliedSchedule, schedules],
  );

  return (
    <Stack spacing={3}>
      {brands.map((brand, index) => {
        return (
          <Box key={`${brand.value}-${index}`}>
            <Typography variant="h2" gutterBottom>
              {brand.title}
            </Typography>
            <NewScheduleForm
              brands={[brand.value]}
              disabled={shouldMigrateSchedule || editing}
              offerId={offerId}
              onSave={onSave}
              onSuccess={onSuccess}
              type={LIST_VISIBILITY}
              regions={[brand.region]}
              start={getScheduleValue(brand.value, 'start')}
              end={getScheduleValue(brand.value, 'end')}
              idList={scheduleIdsByBrand[brand.value] ?? []}
              isDeleteAllowed
              enableUtilBtn
              onApplyToAll={onApplyToAll}
              schedules={schedules}
              brand={brand.value}
            />
          </Box>
        );
      })}
    </Stack>
  );
}
